import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Box, CircularProgress, Typography } from '@mui/material';

/**
 * Componente que protege rutas requiriendo autenticación
 * Si el usuario no está autenticado, redirige automáticamente al login
 *
 * @param {React.ReactNode} children - Componentes hijos a renderizar si está autenticado
 * @param {number[]} allowedLevels - Niveles de usuario permitidos (opcional)
 */
const ProtectedRoute = ({ children, allowedLevels = null }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Mostrar loading mientras se verifica la sesión
  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Verificando sesión...
        </Typography>
      </Box>
    );
  }

  // Si no hay usuario, redirigir a login
  if (!user) {
    console.log('🚪 ProtectedRoute - Redirigiendo a login desde:', location.pathname);
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Verificar nivel de usuario si se especificó
  // El token JWT usa 'nivel' no 'nivel_usuario'
  if (allowedLevels && !allowedLevels.includes(user.nivel)) {
    console.log('⛔ ProtectedRoute - Usuario sin permisos. Nivel:', user.nivel, '| Permitidos:', allowedLevels);
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        gap={2}
        p={3}
      >
        <Typography variant="h4" color="error">
          Acceso Denegado
        </Typography>
        <Typography variant="body1" color="text.secondary">
          No tienes permisos para acceder a esta página.
        </Typography>
      </Box>
    );
  }

  // Usuario autenticado y con permisos, renderizar hijos
  return <>{children}</>;
};

export default ProtectedRoute;
